import { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ShieldCheck, RefreshCw, BookOpen, Settings, Trash2, Ban, CheckCircle2, Save } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Switch } from "@/components/ui/switch";

const when = (iso) => (iso ? new Date(iso).toLocaleString() : "Never");
const TABS = [["overview", "Overview"], ["activity", "Activity"], ["coaches", "Coaches"], ["teams", "Teams"], ["settings", "App settings"]];

function Stat({ label, value, id }) {
  return <div className="card p-4" data-testid={`admin-stat-${id}`}><p className="label">{label}</p><p className="font-display text-4xl font-extrabold">{value}</p></div>;
}

function Overview({ data }) {
  if (!data) return null;
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="admin-overview">
      <Stat id="coaches" label="Coach accounts" value={data.coaches} /><Stat id="disabled" label="Disabled" value={data.disabled} />
      <Stat id="teams" label="Teams" value={data.teams} /><Stat id="plays" label="Plays" value={data.plays} />
      <Stat id="links" label="Active invite links" value={data.share_links} /><Stat id="codes" label="Active access codes" value={data.access_codes} />
      <Stat id="activity" label="Actions (24h)" value={data.activity_24h} /><Stat id="viewers" label="Playbook opens (24h)" value={data.viewer_opens_24h} />
    </div>
  );
}

function Activity({ rows }) {
  return (
    <div className="card overflow-x-auto" data-testid="admin-activity">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-slate-400 border-b border-[#1F3358]">{["When", "Who", "What", "On", "Result"].map((h) => <th key={h} className="p-3 font-bold">{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-[#1F3358]/60" data-testid="admin-activity-row">
              <td className="p-3 whitespace-nowrap text-slate-400">{when(r.at)}</td><td className="p-3">{r.actor}</td><td className="p-3">{r.label}</td>
              <td className="p-3 text-slate-300">{r.target}</td>
              <td className="p-3">{r.status < 400 ? <span className="text-[#4ADE80]">OK</span> : <span className="text-red-300">Failed ({r.status})</span>}</td>
            </tr>
          ))}
          {!rows.length && <tr><td className="p-4 text-slate-400" colSpan={5}>No activity yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function Coaches({ rows, reload }) {
  const toggle = async (u) => {
    try { await api.patch(`/admin/users/${u.id}`, { disabled: !u.disabled }); toast.success(u.disabled ? "Account enabled" : "Account disabled"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-2" data-testid="admin-coaches">
      {rows.map((u) => (
        <div key={u.id} className="card p-4 flex flex-wrap items-center gap-3" data-testid={`admin-user-${u.email}`}>
          <div className="flex-1 min-w-[200px]">
            <p className="font-bold">{u.email} {u.role === "admin" && <span className="text-xs text-[#FACC15] ml-1">ADMIN</span>} {u.disabled && <span className="text-xs text-red-300 ml-1">DISABLED</span>}</p>
            <p className="text-xs text-slate-400">{u.name && `${u.name} · `}Joined {when(u.created_at)} · Last sign-in {when(u.last_login)} · Owns {u.teams_owned} · Assists {u.teams_assisting}</p>
          </div>
          {u.role !== "admin" && (
            <button className={`btn ${u.disabled ? "btn-ghost" : "btn-danger"}`} onClick={() => toggle(u)} data-testid={`admin-toggle-user-${u.email}`}>
              {u.disabled ? <><CheckCircle2 size={16} />Enable</> : <><Ban size={16} />Disable</>}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

function Teams({ rows, reload }) {
  const [del, setDel] = useState(null);
  const remove = async () => {
    try { await api.delete(`/teams/${del.id}`); toast.success("Team deleted"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-2" data-testid="admin-teams">
      {rows.map((t) => (
        <div key={t.id} className="card p-4 flex flex-wrap items-center gap-3" data-testid={`admin-team-${t.id}`}>
          <div className="flex-1 min-w-[200px]">
            <p className="font-bold">{t.name}</p>
            <p className="text-xs text-slate-400">{[t.age_group, t.format, `${t.player_count} per side`].filter(Boolean).join(" · ")} · Head coach {t.owner_email} · {t.coaches} assistants · {t.plays} plays · Sharing {t.sharing ? "on" : "off"}</p>
          </div>
          <Link to={`/teams/${t.id}/playbook`} className="btn btn-ghost" data-testid={`admin-team-playbook-${t.id}`}><BookOpen size={16} />Playbook</Link>
          <Link to={`/teams/${t.id}/settings`} className="btn btn-ghost" data-testid={`admin-team-settings-${t.id}`}><Settings size={16} />Settings</Link>
          <button className="btn btn-danger px-3" onClick={() => setDel(t)} aria-label="Delete team" data-testid={`admin-team-delete-${t.id}`}><Trash2 size={16} /></button>
        </div>
      ))}
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} title="Delete this team?" body={`"${del?.name}" and all of its plays will be removed for good.`} confirmLabel="Delete team" onConfirm={remove} testId="admin-confirm-delete-team" />
    </div>
  );
}

function AppSettings({ value, onSaved }) {
  const [form, setForm] = useState(value);
  useEffect(() => setForm(value), [value]);
  if (!form) return null;
  const save = async () => { try { const { data } = await api.put("/admin/settings", form); onSaved(data); toast.success("App settings saved"); } catch (e) { toast.error(errMsg(e)); } };
  const Row = ({ k, title, desc }) => (
    <label className="flex items-start justify-between gap-4 py-3 border-b border-[#1F3358]">
      <span><span className="font-bold block">{title}</span><span className="text-sm text-slate-400">{desc}</span></span>
      <Switch checked={form[k]} onCheckedChange={(v) => setForm({ ...form, [k]: v })} data-testid={`admin-setting-${k}`} />
    </label>
  );
  return (
    <div className="card p-5 max-w-2xl space-y-2" data-testid="admin-settings">
      <Row k="allow_registration" title="Allow new coach sign-ups" desc="Turn off to stop anyone new from creating a coach account." />
      <Row k="sharing_enabled" title="Team sharing (players & parents)" desc="Turn off to pause every invite link and access code across the app." />
      <div className="pt-3">
        <label className="label" htmlFor="announce">Announcement for all coaches</label>
        <textarea id="announce" className="input py-2 min-h-[72px]" maxLength={300} value={form.announcement} onChange={(e) => setForm({ ...form, announcement: e.target.value })} placeholder="e.g. Practice plays updated for the new season!" data-testid="admin-setting-announcement" />
        <p className="text-xs text-slate-500 mt-1">Shown as a banner at the top of every coach screen. Leave empty to hide it.</p>
      </div>
      <button className="btn btn-primary mt-3" onClick={save} data-testid="admin-settings-save"><Save size={18} />Save settings</button>
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const [d, setD] = useState({ overview: null, activity: [], users: [], teams: [], settings: null });
  const load = useCallback(async () => {
    try {
      const [o, a, u, t, s] = await Promise.all(["/admin/overview", "/admin/activity", "/admin/users", "/admin/teams", "/admin/settings"].map((p) => api.get(p)));
      setD({ overview: o.data, activity: a.data, users: u.data, teams: t.data, settings: s.data });
    } catch (e) { toast.error(errMsg(e)); }
  }, []);
  useEffect(() => { if (user?.role !== "admin") return undefined; load(); const id = setInterval(load, 30000); return () => clearInterval(id); }, [load, user]);
  if (user?.role !== "admin") return <Navigate to="/" replace />;

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div><p className="label flex items-center gap-1"><ShieldCheck size={14} />Administrator</p><h1 className="h1">Admin dashboard</h1><p className="text-sm text-slate-400 mt-1">Updates automatically every 30 seconds.</p></div>
        <button className="btn btn-ghost" onClick={load} data-testid="admin-refresh"><RefreshCw size={18} />Refresh</button>
      </div>
      <div className="flex flex-wrap gap-2 mb-6" role="tablist">
        {TABS.map(([k, l]) => <button key={k} role="tab" aria-selected={tab === k} className={`chip ${tab === k ? "chip-on" : ""}`} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}>{l}</button>)}
      </div>
      {tab === "overview" && <><Overview data={d.overview} /><h2 className="h2 mt-8 mb-3">Latest activity</h2><Activity rows={d.activity.slice(0, 10)} /></>}
      {tab === "activity" && <Activity rows={d.activity} />}
      {tab === "coaches" && <Coaches rows={d.users} reload={load} />}
      {tab === "teams" && <Teams rows={d.teams} reload={load} />}
      {tab === "settings" && <AppSettings value={d.settings} onSaved={(s) => setD({ ...d, settings: s })} />}
    </div>
  );
}
